
import React, { useEffect, useState, useRef } from 'react';
import Picker from 'react-mobile-picker';
import { useTranslation } from 'react-i18next';
import SolidChevron from '../icons/SolidChevron';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCanRefresh } from '../../actions/appActions';
import './GeneralSelectorPicker.css';

const GeneralSelectorPicker = ({
  options = [],
  selectedValue,
  onChange,
  placeholder,
  key = 'option',
  useFirstOptionAsPlaceholder = false,
  backgroundAndBorder = true,
  usePortal = true,
  styles = {},
  disabled = false,
  lockIfSingle = false,
}) => {
  const appCanRefresh = useSelector((state) => state.app.canRefresh);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isPickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const initialPlaceholder = useFirstOptionAsPlaceholder ? options[0]?.label : placeholder;
  const [pickerValue, setPickerValue] = useState({ [key]: initialPlaceholder || selectedValue });

  const handleDone = () => {
    const selectedOption = pickerValue[key];

    if (selectedOption) {
      const selectedObj = options.find((opt) => opt.value === selectedOption);
      onChange(selectedObj);
    }

    setPickerOpen(false);
  };

  const handleChange = (value) => {
    setPickerValue({ [key]: value[key] });
  };


  useEffect(() => {
    if (lockIfSingle && options.length === 1) {
      setPickerValue({ [key]: options[0].value });
      onChange?.(options[0]);
    }
  }, [lockIfSingle, options]);

  useEffect(() => {
    if (selectedValue !== undefined) {
      setPickerValue({ [key]: selectedValue?.value } || '');
    } else {
      setPickerValue({ [key]: '' });
    }
  }, [selectedValue]);

  useEffect(() => {
    if (appCanRefresh === true) {
      dispatch(setCanRefresh(false));
      return () => {
        dispatch(setCanRefresh(true));
      };
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = isPickerOpen ? 'hidden' : 'auto';
    return () => (document.body.style.overflow = 'auto');
  }, [isPickerOpen]);

  const pickerContent = (
    <div ref={pickerRef} className={`book-selector font-medium ${isPickerOpen ? 'open' : ''}`}>
      {isPickerOpen && (
        <>
          <div
            className='text-white semi-bold font-normal text-right py-2 px-3 backgroundTop-selectorPicker'
            onClick={handleDone}
          >
            {t('done')}
          </div>
          <Picker style={{ fontWeight: 500 }} value={pickerValue} onChange={handleChange}>
            <Picker.Column key={key} name={key}>
              {options.map((opt, i) => (
                <Picker.Item key={i} value={opt.value}>
                  {({ selected }) => (
                    <div style={{ color: selected ? '#fff' : '#7a7676' }}>{opt.label}</div>
                  )}
                </Picker.Item>
              ))}
            </Picker.Column>
          </Picker>
        </>
      )}
    </div>
  );

  const opacity = disabled ? 0.5 : 1;
  const isLocked = lockIfSingle && options.length === 1;

  return (
    <>
      <div
        className={`${backgroundAndBorder ? 'select-picker' : ''}`}
        onClick={() => !disabled && !isLocked && setPickerOpen(true)}
        style={{
          ...styles,
          opacity: opacity,
          cursor: disabled || isLocked ? 'default' : 'pointer',
        }}
      >
        {pickerValue[key]
          ? options.find((opt) => opt.value === pickerValue[key])?.label
          : t(initialPlaceholder) || placeholder}
        {!isLocked && <SolidChevron size={18} color='var(--color-primary)' />}
      </div>

      {isPickerOpen && <div onClick={() => setPickerOpen(false)} />}
      {usePortal ? (
        <PortalWrapper portalRootId='bookSelector'>{pickerContent}</PortalWrapper>
      ) : (
        pickerContent
      )}
    </>
  );
};

export default GeneralSelectorPicker;

const PortalWrapper = ({ children, portalRootId }) => {
  const portalRoot = document.getElementById(portalRootId);
  return portalRoot ? createPortal(children, portalRoot) : null;
};


// Es un selector personalizado que permite elegir un valor entre varias opciones.

// |         Propiedad             |      Tipo      |                             Descripción                         |
// | ------------------------------| ---------------| --------------------------------------------------------------- |
// |         `options`             | `Array`        | Lista de opciones que el selector mostrará. Cada opción debe ser un objeto con propiedades `value` y `label`. |
// |       `selectedValue`         | `Object`       | Valor preseleccionado. Debe ser un objeto con la propiedad `value`. Ejemplo: `{ value: 'instagram' }`. |
// |        `onChange`             | `Function`     | Función que se ejecuta cuando se selecciona una opción. Recibe como parámetro el objeto seleccionado. |
// |        `placeholder`          | `String`       | Texto que se muestra cuando no hay un valor seleccionado. |
// | `useFirstOptionAsPlaceholder` | `Boolean`      | Si es `true`, la primera opción de la lista se usará como valor predeterminado si no se pasa un `selectedValue`. Si es `false`, se usará el `placeholder`. |
// |    `backgroundAndBorder`      | `Boolean`      | Si es `true`, se aplica una clase de estilo adicional para tener fondo y borde. |
// |        `usePortal`            | `Boolean`      | Si es `true`, el `Picker` se renderiza dentro de un portal, es decir, fuera de la jerarquía DOM principal. Si es `false`, se renderiza directamente en el componente. |

